// Everything a person has done lives here first, on their own device, and works with no network at all.
// When they have an account, sync.ts pushes what changed and pulls what other devices did.

import { useMemo, useSyncExternalStore } from "react";
import {
  DEFAULT_SETTINGS, emptyProgress, mergeDay, mergeProgress, sameDay, sameProgress,
  type DayRow, type Family, type ProgressRecord, type Settings
} from "../../shared/model";
import { dayKey } from "./clock";

const KEY = "ponder.v1";

export interface LocalData {
  v: 1;
  /** "guest" until someone signs in, then that account's id */
  owner: string;
  device: string;
  progress: Record<string, ProgressRecord>;
  /** thinking time recorded on this device, by day */
  days: Record<string, DayRow>;
  /** thinking time recorded on the person's other devices: day → device → row */
  others: Record<string, Record<string, DayRow>>;
  settings: Settings;
  settingsAt: number;
  /** which puzzle each family offered on a given day, so "today's three" stay put all day */
  assign: Record<string, Partial<Record<Family, string>>>;
  dirtyProgress: string[];
  dirtyDays: string[];
  settingsDirty: boolean;
  /** server clock of the last successful sync */
  lastSync: number;
  name: string;
}

function randomDevice(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(10));
  return [...bytes].map((b) => (b % 36).toString(36)).join("");
}

function fresh(device: string = randomDevice()): LocalData {
  return {
    v: 1, owner: "guest", device, progress: {}, days: {}, others: {}, settings: { ...DEFAULT_SETTINGS }, settingsAt: 0,
    assign: {}, dirtyProgress: [], dirtyDays: [], settingsDirty: false, lastSync: 0, name: ""
  };
}

function load(): LocalData {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const d = JSON.parse(raw) as LocalData;
      if (d && d.v === 1 && d.device) return { ...fresh(d.device), ...d, settings: { ...DEFAULT_SETTINGS, ...d.settings } };
    }
  } catch {
    /* private mode or damaged: start over */
  }
  return fresh();
}

let data: LocalData = load();
let version = 0;
const listeners = new Set<() => void>();
let saveTimer: ReturnType<typeof setTimeout> | null = null;

export function flush(): void {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = null;
  try {
    localStorage.setItem(KEY, JSON.stringify(data));
  } catch {
    /* storage full or blocked: the session still works, it just won't survive a reload */
  }
}

function changed(): void {
  version++;
  if (!saveTimer) saveTimer = setTimeout(flush, 600);
  listeners.forEach((l) => l());
}

export function get(): LocalData {
  return data;
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** re-render on any change; `pick` is only re-run when something actually changed */
export function useStore<T>(pick: (d: LocalData) => T): T {
  const v = useSyncExternalStore(subscribe, () => version, () => 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => pick(data), [v]);
}

const mark = (list: string[], id: string): string[] => (list.includes(id) ? list : [...list, id]);

// ---- progress ----

export function getProgress(id: string): ProgressRecord | undefined {
  return data.progress[id];
}

export function patchProgress(id: string, family: Family, patch: Partial<ProgressRecord>, opts: { touch?: boolean } = {}): ProgressRecord {
  const cur = data.progress[id] ?? emptyProgress(id, family);
  const next: ProgressRecord = { ...cur, ...patch, id, family };
  if (opts.touch !== false) next.updatedAt = Date.now();
  data = { ...data, progress: { ...data.progress, [id]: next }, dirtyProgress: mark(data.dirtyProgress, id) };
  changed();
  return next;
}

// ---- thinking time ----

/** add thinking seconds for today (this device) and for the puzzle being worked on */
export function addThinking(family: Family, puzzleId: string | null, seconds: number, day: string = dayKey()): void {
  if (seconds <= 0) return;
  const row = data.days[day] ?? { day, device: data.device, cut: 0, stars: 0, coin: 0, solved: 0 };
  const days = { ...data.days, [day]: { ...row, [family]: row[family] + seconds } };
  let progress = data.progress;
  let dirtyProgress = data.dirtyProgress;
  if (puzzleId) {
    const cur = progress[puzzleId] ?? emptyProgress(puzzleId, family);
    progress = { ...progress, [puzzleId]: { ...cur, seconds: cur.seconds + seconds } };
    dirtyProgress = mark(dirtyProgress, puzzleId);
  }
  data = { ...data, days, progress, dirtyProgress, dirtyDays: mark(data.dirtyDays, day) };
  changed();
}

export function countSolvedToday(day: string = dayKey()): void {
  const row = data.days[day] ?? { day, device: data.device, cut: 0, stars: 0, coin: 0, solved: 0 };
  data = { ...data, days: { ...data.days, [day]: { ...row, solved: row.solved + 1 } }, dirtyDays: mark(data.dirtyDays, day) };
  changed();
}

// ---- settings, assignment, name ----

export function setSettings(patch: Partial<Settings>): void {
  data = { ...data, settings: { ...data.settings, ...patch }, settingsAt: Date.now(), settingsDirty: true };
  changed();
}

export function setName(name: string): void {
  data = { ...data, name };
  changed();
}

export function setAssignment(day: string, a: Partial<Record<Family, string>>): void {
  data = { ...data, assign: { ...data.assign, [day]: { ...data.assign[day], ...a } } };
  changed();
}

// ---- accounts and sync ----

export function dirtyPayload(): { progress: ProgressRecord[]; days: DayRow[]; settings: { value: Settings; at: number } | null } {
  return {
    progress: data.dirtyProgress.map((id) => data.progress[id]).filter(Boolean),
    days: data.dirtyDays.map((d) => data.days[d]).filter(Boolean),
    settings: data.settingsDirty ? { value: data.settings, at: data.settingsAt } : null
  };
}

export interface SyncSnapshot {
  progress: ProgressRecord[];
  days: DayRow[];
  settings: { value: Partial<Settings>; at: number };
}

/** what a sync request carried, so we only clear "needs sending" for things that haven't changed since */
export interface Sent {
  progress: ProgressRecord[];
  days: DayRow[];
  settingsAt: number | null;
}

/** call after a successful sync: merge what came back, and keep sending anything the server's copy is missing */
export function applySync(sent: Sent, res: SyncSnapshot & { now: number; name?: string }, expectOwner?: string): { resend: boolean } {
  // a reply that was in flight while someone signed out (or another account signed in) must not put its data on this device
  if (expectOwner !== undefined && data.owner !== expectOwner) return { resend: false };
  const progress = { ...data.progress };
  let dirtyProgress = data.dirtyProgress.filter((id) => !sent.progress.some((r) => r.id === id && data.progress[id] === r));
  let resend = false;
  for (const remote of res.progress) {
    const local = progress[remote.id];
    const merged = mergeProgress(local, remote);
    if (!local || !sameProgress(local, merged)) progress[remote.id] = merged;
    // the server's copy lacks something we have (two devices raced): send it again
    if (!sameProgress(merged, remote)) {
      dirtyProgress = mark(dirtyProgress, remote.id);
      resend = true;
    }
  }
  const days = { ...data.days };
  const others = { ...data.others };
  let dirtyDays = data.dirtyDays.filter((d) => !sent.days.some((r) => r.day === d && data.days[d] === r));
  for (const row of res.days) {
    if (row.device === data.device) {
      const local = days[row.day];
      const merged = local ? mergeDay(local, row) : row;
      if (!local || !sameDay(local, merged)) days[row.day] = merged;
      if (!sameDay(merged, row)) {
        dirtyDays = mark(dirtyDays, row.day);
        resend = true;
      }
    } else {
      const dayMap = { ...(others[row.day] ?? {}) };
      dayMap[row.device] = dayMap[row.device] ? mergeDay(dayMap[row.device], row) : row;
      others[row.day] = dayMap;
    }
  }
  let { settings, settingsAt, settingsDirty } = data;
  if (sent.settingsAt !== null && data.settingsAt === sent.settingsAt) settingsDirty = false;
  if (res.settings && res.settings.at > settingsAt) {
    settings = { ...settings, ...res.settings.value };
    settingsAt = res.settings.at;
    settingsDirty = false;
  }
  data = {
    ...data, progress, days, others, dirtyProgress, dirtyDays, settings, settingsAt, settingsDirty,
    lastSync: res.now, name: res.name ?? data.name
  };
  changed();
  return { resend };
}

/** first sign-in on a device: everything done as a guest now belongs to the account and gets uploaded */
export function adoptOwner(userId: string, name: string): void {
  if (data.owner === userId) {
    if (name && name !== data.name) setName(name);
    return;
  }
  if (data.owner === "guest") {
    data = {
      ...data, owner: userId, name, lastSync: 0,
      dirtyProgress: Object.keys(data.progress), dirtyDays: Object.keys(data.days), settingsDirty: data.settingsAt > 0
    };
  } else {
    // someone else's data was on this device: never mix accounts
    data = { ...fresh(data.device), owner: userId, name };
  }
  changed();
  flush();
}

/** on sign-out the device goes back to being an empty guest device; the account keeps everything */
export function resetToGuest(): void {
  data = fresh(data.device);
  changed();
  flush();
}

export function wipeAll(): void {
  data = fresh();
  changed();
  flush();
}

/**
 * Another tab of the same browser saved its copy: fold it into ours instead of letting either tab overwrite the other.
 * Same rules as syncing (progress merges without loss, thinking seconds only go up), so nothing a person did is dropped.
 */
export function mergeFromOtherTab(raw: string | null): void {
  if (!raw) return;
  let other: LocalData;
  try {
    other = JSON.parse(raw) as LocalData;
  } catch {
    return;
  }
  if (!other || other.v !== 1 || other.device !== data.device || other.owner !== data.owner) return;
  const progress = { ...data.progress };
  for (const [id, rec] of Object.entries(other.progress ?? {})) {
    const local = progress[id];
    const merged = mergeProgress(local, rec);
    if (!local || !sameProgress(local, merged)) progress[id] = merged;
  }
  const days = { ...data.days };
  for (const [day, row] of Object.entries(other.days ?? {})) days[day] = days[day] ? mergeDay(days[day], row) : row;
  const others = { ...data.others };
  for (const [day, byDevice] of Object.entries(other.others ?? {})) {
    const cur = { ...(others[day] ?? {}) };
    for (const [dev, row] of Object.entries(byDevice)) cur[dev] = cur[dev] ? mergeDay(cur[dev], row) : row;
    others[day] = cur;
  }
  const newerSettings = (other.settingsAt ?? 0) > data.settingsAt;
  data = {
    ...data,
    progress,
    days,
    others,
    settings: newerSettings ? { ...data.settings, ...other.settings } : data.settings,
    settingsAt: newerSettings ? other.settingsAt : data.settingsAt,
    assign: { ...other.assign, ...data.assign },
    dirtyProgress: [...new Set([...data.dirtyProgress, ...(other.dirtyProgress ?? [])])],
    dirtyDays: [...new Set([...data.dirtyDays, ...(other.dirtyDays ?? [])])],
    settingsDirty: data.settingsDirty || (newerSettings && other.settingsDirty),
    lastSync: Math.max(data.lastSync, other.lastSync ?? 0),
    name: data.name || other.name || ""
  };
  version++;
  listeners.forEach((l) => l());
}

// keep progress safe if the tab is closed quickly
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", flush);
  window.addEventListener("storage", (e) => {
    if (e.key === KEY) mergeFromOtherTab(e.newValue);
  });
  document.addEventListener("visibilitychange", () => document.visibilityState === "hidden" && flush());
}
